/**
 * 中文排版行首禁则（简化实现，对齐常见「避头尾」直觉）。
 *
 * 下列符号**允许**出现在行首（与编辑区常见习惯一致）：
 * 弯引号 “ ” ‘ ’、直角引号「『、前括号 ( （ [ 【 〔、前书名号《〈 等。
 * 其余标点（逗号、句号、顿号、句末点号、闭括号、闭引号等）不宜单独出现在行首。
 *
 * 完整与浏览器/CSS `line-break: strict` 一致需 UAX #14 + 引擎实现；此处为启发式。
 */

/** 明确允许居于行首的标点（弯引号/直角引号/前括号/前书名号等「起」类） */
const ALLOWED_LINE_START = new Set(
  [
    '(',
    '（',
    '[',
    '〔',
    '【',
    '《',
    '〈',
    '「',
    '『',
    '\u201c',
    '\u2018', // “ ‘
    '\u300c',
    '\u300e',
    '\u300a',
    '\u3008',
    '\u3014',
    '\u3010',
    '〈',
    '｟',
  ].flatMap((s) => s.split('')),
);

/** 明确不宜在行首出现的标点（闭括、句读、省文等） */
const FORBIDDEN_LINE_START = new Set(
  [
    '，',
    '。',
    '、',
    '．',
    '；',
    '：',
    '！',
    '？',
    '）',
    '」',
    '』',
    '】',
    '〉',
    '》',
    '’',
    '”',
    '…',
    '—',
    '·',
    '～',
    '∶',
    '％',
    '％',
    '＋',
    '－',
    '：',
    '；',
    '、',
  ].flatMap((s) => s.split('')),
);

/**
 * 若 `ch` 作为**新行首字符**是否违反简化禁则（应与前字同行）。
 */
export function isForbiddenLineStartChar(ch: string): boolean {
  if (!ch) return false;
  const c = ch[0];
  if (ALLOWED_LINE_START.has(c)) return false;
  if (FORBIDDEN_LINE_START.has(c)) return true;
  // 其余标点/符号：保守视为不宜单独居首（避免段首出现逗号、句号等）
  if (/[\p{P}]/u.test(c) && !ALLOWED_LINE_START.has(c)) return true;
  return false;
}

/**
 * 在 `remaining` 上已确定「本行可容纳 fitEnd 个字符」时，若下一行首字违反禁则，则减少 fitEnd，使该字留在上一行。
 */
export function adjustBreakForForbiddenLineStart(remaining: string, fitEnd: number): number {
  let e = fitEnd;
  while (e > 0 && remaining[e] !== undefined && isForbiddenLineStartChar(remaining[e])) {
    e -= 1;
  }
  return e;
}

/**
 * 对纯文本行数组做「从上一行末尾借一字」修正，与逐字换行禁则互补。
 */
export function fixCjkLineStartsPullBack(lines: string[]): string[] {
  if (lines.length <= 1) return lines;
  const copy = lines.map((s) => s);
  let changed = true;
  let guard = 0;
  while (changed && guard++ < copy.length * 4) {
    changed = false;
    for (let i = 1; i < copy.length; i++) {
      const line = copy[i];
      if (!line.length) continue;
      const c0 = line[0];
      if (!isForbiddenLineStartChar(c0)) continue;
      const prev = copy[i - 1];
      if (!prev.length) continue;
      if (prev.length === 1) {
        copy[i] = prev + line;
        copy[i - 1] = '';
        changed = true;
        continue;
      }
      const last = prev[prev.length - 1];
      copy[i - 1] = prev.slice(0, -1);
      copy[i] = last + line;
      changed = true;
    }
  }
  return copy.filter((l) => l.length > 0);
}
